import { ChangeEvent, FC, useEffect, useState } from "react";
import FormInput from "../common/FormInput";
import { sendRmaAPI } from "../../api/rma.api";
import { RMAFormData, RMAQueryParams } from "../../utils/models/rma";
import { useGetDdtDetails } from "../../utils/hooks/useGetDdtDetails";
import { RMA_THANK_YOU_PATH } from "../../utils/constants/paths.constants";
import LoadingSpinner from "../common/LoadingSpinner";
import { getRequestedLanguages } from "../../locale/language";
import { useGetCustomerStaticData } from "../../utils/hooks/useGetCustomerStaticData";

const RmaForm: FC = () => {
    const language = getRequestedLanguages("it");
    // Gestione loading e visibilità dati
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingOnDDTDetails, setIsLoadingOnDDTDetails] = useState(false);
    const [isLoadingOnRow, setIsLoadingOnRow] = useState(false);

    // Dati per invio RMA
    const [ddtInfo, setDdtInfo] = useState<RMAQueryParams>();
    const [formData, setFormData] = useState<RMAFormData>({
        vat: "",
        reason: "",
        complaintLinesSerialNo: "",
        complaintQuantity: 1,
    });

    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Recupero dei dati da API/window object
    const { customerStaticData } = useGetCustomerStaticData();
    const { ddtDetails, foundRow } = useGetDdtDetails({
        setIsLoading: setIsLoadingOnDDTDetails,
        ddtId: ddtInfo?.ddtId || "",
        setIsLoadingOnRow: setIsLoadingOnRow,
        documentId: ddtInfo?.documentId,
        ddtLineNo: ddtInfo?.ddtLineNo,
    });

    // Recupero dati da URL
    useEffect(() => {
        const p = new URLSearchParams(window.location.search);
        const ddtLineNo = p.get("ddtLineNo");
        const ddtId = p.get("ddtId");
        const documentId = p.get("documentId");

        if (ddtLineNo && ddtId && documentId) {
            setDdtInfo({
                ddtId,
                ddtLineNo,
                documentId,
            });
        }
    }, []);

    useEffect(() => {
        // I caricamenti sono terminati
        if (!isLoadingOnRow && !isLoadingOnDDTDetails) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
    }, [isLoadingOnRow, isLoadingOnDDTDetails]);

    const handleChange = (
        e: ChangeEvent<HTMLInputElement>,
        key: keyof RMAFormData,
    ) => {
        const newData: Partial<RMAFormData> = {
            [key]: e.target.value,
        };
        setFormData({ ...formData, ...newData });
    };

    const handleSubmit = async () => {
        if (
            !ddtDetails ||
            !customerStaticData?.bcCustomerNumber ||
            !ddtInfo?.ddtLineNo ||
            !foundRow
        )
            return;
        try {
            setErrorMessage(null);
            setIsLoading(true);
            const res = await sendRmaAPI(
                formData,
                ddtDetails,
                customerStaticData?.bcCustomerNumber,
                ddtInfo.ddtLineNo,
                foundRow,
            );

            if (res) {
                // window.open(CREATE_DDT_DETTAGLIO_PATH(ddtInfo.ddtId), "_self");
                window.open(RMA_THANK_YOU_PATH, "_self");
            }
        } catch (error) {
            setErrorMessage(language.rma_form.error_submit);
            console.log("Error in useGetOrderDetails:", error);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) return <LoadingSpinner />;

    if (!ddtDetails) {
        return (
            <div className="w-100 text-center my-4">
                <span>{language.rma_form.error_load_ddt_details}</span>
            </div>
        );
    }

    if (!foundRow) {
        return (
            <div className="w-100 text-center my-4">
                <span>{language.rma_form.error_load_row}</span>
            </div>
        );
    }

    if (
        !ddtInfo ||
        !ddtDetails ||
        !customerStaticData?.bcCustomerNumber ||
        !ddtInfo.documentId
    )
        return (
            <div className="w-100 text-center my-4">
                {!ddtDetails ? (
                    <span>{language.rma_form.error_load_ddt}</span>
                ) : (
                    <span>{language.rma_form.error_missing_data}</span>
                )}
            </div>
        );

    return (
        <div className="d-flex flex-column mb-5">
            {/* Tipo di reclamo e dati cliente */}
            <div className="px-3 py-2 default-border-radius default-border mt-3">
                {/* Codici e riferimenti */}
                <div className=" mt-3">
                    <div className="mt-3">
                        <div className="mt-2 w-100 d-flex flex-column flex-md-row gap-3">
                            <FormInput
                                type="text"
                                label={language.rma_form.vat_label}
                                placeholder={language.rma_form.vat_label}
                                name="vat"
                                handleChange={(e) => handleChange(e, "vat")}
                                value={formData.vat}
                                required={true}
                            />
                            <FormInput
                                type="text"
                                label={language.rma_form.serial_number_label}
                                placeholder={
                                    language.rma_form.serial_number_label
                                }
                                name="serialNo"
                                handleChange={(e) =>
                                    handleChange(e, "complaintLinesSerialNo")
                                }
                                required={false}
                                value={formData.complaintLinesSerialNo || ""}
                            />
                            <FormInput
                                type="number"
                                label={
                                    language.rma_form.quantity_number_label +
                                    foundRow.quantity.toString()
                                }
                                placeholder={
                                    language.rma_form.quantity_number_label +
                                    foundRow.quantity.toString()
                                }
                                name="serialNo"
                                max={foundRow.quantity}
                                handleChange={(e) =>
                                    handleChange(e, "complaintQuantity")
                                }
                                required={true}
                                value={formData.complaintQuantity || ""}
                            />
                        </div>
                        <div className="mt-2 w-100 d-flex flex-column flex-md-row gap-3">
                            <FormInput
                                type="text"
                                label={language.rma_form.reason_label}
                                placeholder={language.rma_form.reason_label}
                                name="reason"
                                handleChange={(e) => handleChange(e, "reason")}
                                value={formData.reason}
                                required={true}
                            />
                        </div>
                    </div>

                    {/* Pulsanti */}
                    <div className="d-flex flex-column mt-3">
                        {errorMessage && (
                            <div
                                className="d-flex w-100 my-4"
                                style={{ color: "red" }}
                            >
                                <span className="mx-auto">{errorMessage}</span>
                            </div>
                        )}
                        <button
                            className="btn btn-danger"
                            onClick={async () => {
                                if (
                                    formData.vat &&
                                    formData.reason &&
                                    Number(formData.complaintQuantity) <=
                                        Number(foundRow.quantity)
                                ) {
                                    await handleSubmit();
                                }
                            }}
                            disabled={
                                !formData.vat ||
                                !formData.reason ||
                                Number(formData.complaintQuantity) >
                                    Number(foundRow.quantity)
                            }
                        >
                            {language.rma_form.submit_button}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RmaForm;
